'use client';

import React, { useState } from 'react';
import { MotionDiv, SafeAnimatePresence } from '@/components/motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { type FAQItem, faqItems, groupedByCategory } from './faqData';

// Component for a single FAQ item
const FAQAccordionItem = ({ item }: { item: FAQItem }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-gray-200 py-4">
      <button
        className="flex justify-between items-center w-full text-left py-2 focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <h3 className="text-lg font-medium text-gray-800">{item.question}</h3>
        <span className="dropdown-chevron">
          {isOpen ? (
            <ChevronUp className="h-5 w-5 text-blue-500" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          )}
        </span>
      </button>
      <SafeAnimatePresence>
        {isOpen && (
          <MotionDiv
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="py-3 text-gray-600">
              {item.answer.split('\n').map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  {index < item.answer.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </div>
          </MotionDiv>
        )}
      </SafeAnimatePresence>
    </div>
  );
};

export default function FAQ() {
  return (
    <section className="py-12">
      <MotionDiv 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-12"
      >
        <h2 className="text-2xl font-bold mb-4">Preguntas Frecuentes</h2>
        <p className="text-gray-600 max-w-3xl mx-auto">
          Encuentre respuestas a las preguntas más comunes sobre nuestros productos, 
          servicios y procesos comerciales.
        </p>
      </MotionDiv>
      
      <div className="max-w-4xl mx-auto">
        {Object.entries(groupedByCategory).map(([category, items], index) => (
          <MotionDiv 
            key={category}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="mb-8"
          >
            <h3 className="text-xl font-semibold mb-4 text-blue-700 border-b border-blue-200 pb-2">
              {category}
            </h3>
            <div className="bg-white rounded-lg shadow-sm p-6">
              {items.map(item => (
                <FAQAccordionItem key={item.id} item={item} />
              ))}
            </div>
          </MotionDiv>
        ))}
      </div>
    </section>
  );
} 